import { useEffect, useRef } from 'react';
import {
  AdaptiveCard as CardRenderer,
  Carousel,
  CarouselPage,
  HostConfig,
} from 'adaptivecards';
import MarkdownIt from 'markdown-it';
import 'adaptivecards/lib/adaptivecards-carousel.css';
import type { AdaptiveCard } from '@/lib/notificationSend.ts';
import teamsDark from './teams/microsoft-teams-dark.json';
import teamsLight from './teams/microsoft-teams-light.json';
import './teams/teamsCard.css';
import { withoutTeamsIcons } from './withoutTeamsIcons.ts';

/*
 * An Adaptive Card drawn by Microsoft's own renderer with the Teams host
 * config. Buttons and links stay inert: a url can be a sign-in link minted for
 * the person the notification went to, which is nobody else's to open.
 */

const markdown = new MarkdownIt({ html: false, linkify: false });

CardRenderer.onProcessMarkdown = (text, result) => {
  result.outputHtml = markdown.render(text);
  result.didProcess = true;
};

/*
 * Teams draws a button set on a carousel page; the renderer refuses one, both
 * on the carousel and on the pages it builds itself, so the rule is lifted there.
 */
for (const type of [Carousel, CarouselPage]) {
  const prototype = type.prototype as unknown as {
    forbiddenChildElements: (this: unknown) => string[];
  };
  const forbidden = prototype.forbiddenChildElements;
  prototype.forbiddenChildElements = function (this: unknown) {
    return forbidden.call(this).filter((name) => name !== 'ActionSet');
  };
}

export interface AdaptiveCardPreviewProps {
  card: AdaptiveCard;
  theme?: 'light' | 'dark';
}

export function AdaptiveCardPreview({ card, theme = 'light' }: AdaptiveCardPreviewProps) {
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = host.current;
    if (!target) return;

    const renderer = new CardRenderer();
    renderer.hostConfig = new HostConfig(theme === 'dark' ? teamsDark : teamsLight);
    renderer.onExecuteAction = () => undefined;
    renderer.onAnchorClicked = () => true;
    renderer.parse(withoutTeamsIcons(card));

    const element = renderer.render();
    target.replaceChildren(...(element ? [element] : []));
    return () => target.replaceChildren();
  }, [card, theme]);

  return <div ref={host} className={`teams-card teams-card-${theme}`} />;
}
